import React, { useEffect, useState } from 'react'
import API from '../../../../services/API'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import GroupsIcon from '@mui/icons-material/Groups'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import MoveDownIcon from '@mui/icons-material/MoveDown'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import BigModal from '../../../../components/Modals/BigModal'

const statusChipColor = (status) =>
  ({
    Active: 'success',
    Available: 'success',
    Assigned: 'info',
    Break: 'warning',
    Unavailable: 'warning',
    'Off Duty': 'default',
    Closed: 'default',
  })[status] || 'default'

const ConflictDetailRow = ({ icon, label, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(35,154,164,0.1)',
        color: '#1E7F88',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Stack sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Stack>
  </Stack>
)

const AddResourcePopup = ({ resource, controlRoomID, open, onClose }) => {
  const [mode, setMode] = useState('callsign')
  const [callSign, setCallSign] = useState('')
  const [vehicles, setVehicles] = useState(null)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  // Set when the vehicle/call sign is already live in another control room —
  // swaps the form for a conflict view. `kind: 'vehicle'` offers move-only
  // (a vehicle can't be in two rooms); `kind: 'callsign'` offers move OR
  // add-a-separate-resource (two events can both run "ROMEO1").
  const [conflict, setConflict] = useState(null)

  const isNew = resource === 'New'

  useEffect(() => {
    if (!open || !isNew || mode !== 'vehicle' || vehicles !== null) return
    API.get('/vehicleList').then((res) => {
      if (res.status === 200 && Array.isArray(res.data)) {
        setVehicles(res.data.filter((v) => v.callSign))
      } else {
        setVehicles([])
        setSubmitError('Could not load the vehicle list. Try again.')
      }
    })
  }, [open, isNew, mode, vehicles])

  if (!isNew) return null

  const resetAndClose = () => {
    setMode('callsign')
    setCallSign('')
    setSelectedVehicle(null)
    setConflict(null)
    setSubmitError('')
    onClose()
  }

  const submitNewResource = (extra = {}) => {
    if (submitting) return
    const isVehicle = mode === 'vehicle'

    setSubmitting(true)
    setSubmitError('')

    API.post('/commandcontrol/newresource', {
      controlRoomID,
      ...(isVehicle
        ? { vehicleID: selectedVehicle._id }
        : { callSign: callSign.trim() }),
      ...extra,
    })
      .then((res) => {
        if (res.status === 200) {
          resetAndClose()
        } else if (
          res.status === 409 &&
          ['vehicleInOtherRoom', 'callSignInOtherRoom'].includes(
            res.data?.errorCode,
          )
        ) {
          setConflict({
            ...res.data.conflict,
            kind:
              res.data.errorCode === 'vehicleInOtherRoom'
                ? 'vehicle'
                : 'callsign',
          })
        } else {
          setSubmitError(
            res.data?.error || 'Could not add the resource. Try again.',
          )
        }
      })
      .finally(() => setSubmitting(false))
  }

  const handleSubmitNew = (e) => {
    e.preventDefault()
    if (mode === 'vehicle' ? !selectedVehicle : !callSign.trim()) return
    submitNewResource()
  }

  const handleMoveHere = () => {
    if (submitting) return
    setSubmitting(true)
    setSubmitError('')

    API.post('/commandcontrol/moveresourcetocontrolroom', {
      controlRoomID,
      resourceID: conflict.resourceID,
    })
      .then((res) => {
        if (res.status === 200) {
          resetAndClose()
        } else {
          setSubmitError(
            res.data?.error || 'Could not move the resource. Try again.',
          )
        }
      })
      .finally(() => setSubmitting(false))
  }

  const cancelButtonSx = {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 800,
    color: '#64748B',
  }
  const submitButtonSx = {
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 900,
    backgroundColor: '#239AA4',
    boxShadow: '0 12px 28px rgba(35,154,164,0.28)',
    '&:hover': {
      backgroundColor: '#1E7F88',
      boxShadow: '0 16px 34px rgba(35,154,164,0.34)',
    },
  }

  return (
    <BigModal open={open} onClose={resetAndClose} autoWidth>
      <Box>
        <Box
          sx={{
            mx: -2,
            mt: -2,
            mb: 2.5,
            p: 2.5,
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #239AA4 150%)',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <LocalHospitalIcon />
            </Box>

            <Stack>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                Control Room Resource
              </Typography>
              <Typography variant="h6" fontWeight={900} color="white">
                {!conflict
                  ? 'Add New Resource'
                  : conflict.kind === 'vehicle'
                    ? 'Vehicle Already In Use'
                    : 'Call Sign Already In Use'}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {conflict ? (
          <Stack gap={2}>
            {conflict.kind === 'vehicle' ? (
              <Alert severity="warning">
                <strong>{conflict.callSign}</strong> is already live in{' '}
                {conflict.controlRoom?.name || 'another control room'} — a
                vehicle can only be in one control room at a time.
              </Alert>
            ) : (
              <Alert severity="info">
                <strong>{conflict.callSign}</strong> is already live in{' '}
                {conflict.controlRoom?.name || 'another control room'}. You
                can add a separate {conflict.callSign} for this control room,
                or move that resource here.
              </Alert>
            )}

            <Stack
              gap={1.5}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ConflictDetailRow
                icon={<LocalHospitalIcon fontSize="small" />}
                label="Resource"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={800}>
                    {conflict.callSign}
                  </Typography>
                  <Chip
                    size="small"
                    label={conflict.status}
                    color={statusChipColor(conflict.status)}
                  />
                </Stack>
              </ConflictDetailRow>

              <ConflictDetailRow
                icon={<MeetingRoomIcon fontSize="small" />}
                label="Control room"
              >
                {conflict.controlRoom ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={800} noWrap>
                      {conflict.controlRoom.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={conflict.controlRoom.status}
                      color={statusChipColor(conflict.controlRoom.status)}
                    />
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Not in a control room
                  </Typography>
                )}
              </ConflictDetailRow>

              <ConflictDetailRow
                icon={<GroupsIcon fontSize="small" />}
                label="Assigned crew"
              >
                <Typography variant="body2">
                  {conflict.crew?.length ? (
                    conflict.crew.join(', ')
                  ) : (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      No one assigned
                    </Typography>
                  )}
                </Typography>
              </ConflictDetailRow>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {conflict.kind === 'callsign'
                ? `Adding a separate resource keeps both control rooms running their own ${conflict.callSign}, each with its own crew and status. Moving instead will remove it from ${
                    conflict.controlRoom?.name || 'its current control room'
                  }, unassign any crew, and set its status to Off Duty.`
                : `Moving it here will remove it from ${
                    conflict.controlRoom?.name || 'its current control room'
                  }, unassign any crew, and set its status to Off Duty. A log entry will be added in both control rooms.`}
            </Typography>

            {submitError && <Alert severity="error">{submitError}</Alert>}

            <Divider />

            <Stack spacing={1.5}>
              {conflict.kind === 'callsign' && (
                <Button
                  fullWidth
                  onClick={() => submitNewResource({ createSeparate: true })}
                  disabled={submitting}
                  variant="contained"
                  endIcon={
                    submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <AddIcon />
                    )
                  }
                  sx={submitButtonSx}
                >
                  Add as Separate Resource
                </Button>
              )}

              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1.5}
              >
                <Button
                  fullWidth
                  onClick={() => setConflict(null)}
                  startIcon={<CloseIcon />}
                  disabled={submitting}
                  sx={cancelButtonSx}
                >
                  Cancel
                </Button>

                <Button
                  fullWidth
                  onClick={handleMoveHere}
                  disabled={submitting}
                  variant={
                    conflict.kind === 'callsign' ? 'outlined' : 'contained'
                  }
                  endIcon={
                    submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <MoveDownIcon />
                    )
                  }
                  sx={
                    conflict.kind === 'callsign'
                      ? {
                          borderRadius: 999,
                          textTransform: 'none',
                          fontWeight: 800,
                          borderColor: '#239AA4',
                          color: '#1E7F88',
                          '&:hover': {
                            borderColor: '#1E7F88',
                            backgroundColor: 'rgba(35,154,164,0.06)',
                          },
                        }
                      : submitButtonSx
                  }
                >
                  Move to This Control Room
                </Button>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Stack gap={2} component="form" onSubmit={handleSubmitNew}>
            <Typography variant="body2" color="text.secondary">
              Add an operational resource to this control room — either a
              blank call sign, or one of your fleet vehicles.
            </Typography>

            <ToggleButtonGroup
              value={mode}
              exclusive
              fullWidth
              onChange={(e, next) => {
                if (!next) return
                setMode(next)
                setSubmitError('')
              }}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: 3,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(35,154,164,0.12)',
                    color: '#1E7F88',
                  },
                },
              }}
            >
              <ToggleButton value="callsign">
                <LocalHospitalIcon fontSize="small" sx={{ mr: 1 }} />
                Call Sign
              </ToggleButton>
              <ToggleButton value="vehicle">
                <DirectionsCarIcon fontSize="small" sx={{ mr: 1 }} />
                Vehicle
              </ToggleButton>
            </ToggleButtonGroup>

            {submitError && <Alert severity="error">{submitError}</Alert>}

            {mode === 'vehicle' ? (
              <Autocomplete
                options={vehicles ?? []}
                loading={vehicles === null}
                value={selectedVehicle}
                onChange={(e, next) => setSelectedVehicle(next)}
                getOptionLabel={(v) =>
                  [v.callSign, v.regPlate].filter(Boolean).join(' — ')
                }
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehicle"
                    placeholder="Search by call sign or reg"
                    autoFocus
                    InputProps={{
                      ...params.InputProps,
                      sx: { borderRadius: 3 },
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <DirectionsCarIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                value={callSign}
                onChange={(e) => setCallSign(e.target.value.toUpperCase())}
                label="Call sign"
                placeholder="e.g. AMB 01"
                fullWidth
                autoComplete="off"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalHospitalIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 3,
                  },
                }}
              />
            )}

            <Divider />

            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5}>
              <Button
                fullWidth
                onClick={resetAndClose}
                startIcon={<CloseIcon />}
                disabled={submitting}
                sx={cancelButtonSx}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                fullWidth
                disabled={
                  (mode === 'vehicle' ? !selectedVehicle : !callSign.trim()) ||
                  submitting
                }
                variant="contained"
                endIcon={
                  submitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <AddIcon />
                  )
                }
                sx={submitButtonSx}
              >
                Add Resource
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </BigModal>
  )
}

export default AddResourcePopup
